import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Award, Loader2, Copy, Check, ExternalLink, ShieldCheck, AlertTriangle, Search,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import usePageTitle from '../hooks/usePageTitle';

const BRAND_BLUE = '#2B4B6A';
const TRADOS_LOGO = 'https://www.trados.app/wp-content/uploads/2026/03/Trados-Trade-Verified-square.png';

const TIERS = {
  bronze: { label: 'Bronze', ring: '#CD7F32' },
  silver: { label: 'Silver', ring: '#9FB1C1' },
  gold: { label: 'Gold', ring: '#D4AF37' },
};

// Module scope: a component declared inside the render function remounts its
// subtree on every state change.
const Shell = ({ children }) => (
  <Sidebar>
    <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
      <div className="max-w-3xl mx-auto space-y-4">{children}</div>
    </div>
  </Sidebar>
);

const NetworkBadge = () => {
  usePageTitle('I am part of the Trados network');

  const [state, setState] = useState('loading'); // loading | none | unverified | ready | error
  const [listing, setListing] = useState(null);
  const [badge, setBadge] = useState(null);
  const [theme, setTheme] = useState('light');
  const [size, setSize] = useState('md');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const res = await api.get('/directory/my-listing');
      const l = res.data.listing;
      if (!l || !l.is_owner) { setState('none'); return; }
      setListing(l);

      const b = await api.get(`/public/directory/${l.slug}/badge`);
      setBadge(b.data);
      setState(b.data?.verified ? 'ready' : 'unverified');
    } catch (err) {
      setState('error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const snippet = listing
    ? `<div class="trados-network-badge"\n     data-company="${listing.slug}"${theme === 'dark' ? '\n     data-theme="dark"' : ''}${size === 'sm' ? '\n     data-size="sm"' : ''}></div>\n<script src="https://www.trados.app/widgets/trados-badge.js" async></script>`
    : '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success('Embed code copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — select the code and copy manually');
    }
  };

  if (state === 'loading') {
    return (
      <Shell>
        <div className="flex flex-col items-center justify-center py-24 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mb-2" />
          <p className="text-sm">Checking your verification...</p>
        </div>
      </Shell>
    );
  }

  if (state === 'error') {
    return (
      <Shell>
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-red-600">Could not load your badge</p>
          <button onClick={load} className="mt-3 px-3 py-1.5 text-xs font-medium text-white rounded-lg"
                  style={{ backgroundColor: BRAND_BLUE }}>Try again</button>
        </div>
      </Shell>
    );
  }

  if (state === 'none') {
    return (
      <Shell>
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <Award className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-gray-900">Claim your business first</h1>
          <p className="text-sm text-gray-500 mt-1.5 max-w-md mx-auto">
            The network badge is available once you own an approved listing in the Trados directory.
          </p>
          <Link to="/directory"
                className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 rounded-lg text-sm font-medium text-white"
                style={{ backgroundColor: BRAND_BLUE }}>
            <Search className="w-4 h-4" /> Find my business
          </Link>
        </div>
      </Shell>
    );
  }

  if (state === 'unverified') {
    return (
      <Shell>
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <ShieldCheck className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-gray-900">Get verified to unlock your badge</h1>
          <p className="text-sm text-gray-500 mt-1.5 max-w-lg mx-auto">
            The badge shows your verification medal — Bronze, Silver or Gold. Until{' '}
            <strong>{listing?.name}</strong> is verified there is no badge to display, and the
            widget deliberately renders nothing rather than an empty or unverified mark.
          </p>
          <Link to="/admin/verify/bronze"
                className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 rounded-lg text-sm font-medium text-white"
                style={{ backgroundColor: BRAND_BLUE }}>
            Start verification
          </Link>
        </div>
      </Shell>
    );
  }

  const tier = TIERS[badge.tier] || TIERS.bronze;

  return (
    <Shell>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Award className="w-5 h-5" style={{ color: BRAND_BLUE }} />
            I am part of the Trados network
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Add your verification badge to your own website. It always reflects your live status.
          </p>
        </div>
        <a href={badge.url} target="_blank" rel="noopener noreferrer"
           className="text-sm font-medium hover:underline flex items-center gap-1" style={{ color: BRAND_BLUE }}>
          View listing <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>

      {/* Preview */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-900">Preview</h2>
          <div className="flex items-center gap-2">
            {['light', 'dark'].map((t) => (
              <button key={t} onClick={() => setTheme(t)} data-testid={`badge-theme-${t}`}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${
                        theme === t ? 'border-blue-400 text-blue-700 bg-blue-50' : 'border-gray-200 text-gray-500'}`}>
                {t}
              </button>
            ))}
            {['md', 'sm'].map((sz) => (
              <button key={sz} onClick={() => setSize(sz)} data-testid={`badge-size-${sz}`}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${
                        size === sz ? 'border-blue-400 text-blue-700 bg-blue-50' : 'border-gray-200 text-gray-500'}`}>
                {sz}
              </button>
            ))}
          </div>
        </div>

        <div className={`rounded-xl p-6 flex justify-center ${theme === 'dark' ? 'bg-gray-100' : 'bg-gray-50'}`}>
          {/* Static replica of what the widget renders */}
          <a href={badge.url} target="_blank" rel="noopener noreferrer" data-testid="badge-preview"
             className="inline-flex items-center gap-3 no-underline rounded-2xl border transition hover:-translate-y-0.5"
             style={{
               padding: size === 'sm' ? '9px 14px' : '12px 18px',
               background: theme === 'dark' ? BRAND_BLUE : '#fff',
               borderColor: theme === 'dark' ? 'rgba(255,255,255,.16)' : 'rgba(43,75,106,.16)',
               boxShadow: '0 3px 14px rgba(16,30,54,.10)',
             }}>
            <img src={TRADOS_LOGO} alt="Trados"
                 style={{ width: size === 'sm' ? 30 : 38, height: size === 'sm' ? 30 : 38,
                          objectFit: 'contain', borderRadius: 8, flex: '0 0 auto' }} />
            <span className="flex flex-col leading-tight flex-1 min-w-0">
              <span className="text-[11px] font-bold uppercase tracking-wider"
                    style={{ color: theme === 'dark' ? 'rgba(255,255,255,.66)' : '#7c8b9c' }}>
                Part of the Trados network
              </span>
              <span className="font-extrabold text-sm" style={{ color: theme === 'dark' ? '#fff' : BRAND_BLUE }}>
                {badge.name}
              </span>
              <span className="text-[11px] font-bold" style={{ color: tier.ring }}>
                {tier.label} Verified
              </span>
            </span>
            <svg viewBox="0 0 48 48"
                 style={{ width: size === 'sm' ? 32 : 40, height: size === 'sm' ? 32 : 40, flex: '0 0 auto' }}>
              <path d="M16 4h16l-5 14H21L16 4z" fill={tier.ring} opacity=".55" />
              <circle cx="24" cy="30" r="14" fill={tier.ring} />
              <circle cx="24" cy="30" r="10.5" fill="#fff" opacity=".18" />
              <path d="M18.5 30.2l3.6 3.6 7.4-7.6" stroke="#fff" strokeWidth="3"
                    strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
          </a>
        </div>
      </div>

      {/* Embed code */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-900">Embed code</h2>
          <button onClick={copy} data-testid="badge-copy"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white"
                  style={{ backgroundColor: BRAND_BLUE }}>
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Copied' : 'Copy code'}
          </button>
        </div>
        <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs font-mono overflow-x-auto whitespace-pre">
{snippet}
        </pre>
        <p className="text-xs text-gray-400 mt-2">
          Paste anywhere in your site&apos;s HTML — footer, about page, or homepage.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
        <p className="text-xs text-blue-900 leading-relaxed">
          <strong>The badge stays honest automatically.</strong> It reads your live status from
          Trados each time the page loads, so it always shows your current medal. If your
          verification lapses the badge disappears on its own — and because it renders from our
          response rather than from the snippet, nobody can fake one by copying your code.
        </p>
      </div>
    </Shell>
  );
};

export default NetworkBadge;
